import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';
import {Shipment} from '../../../domain/shipment';
import {Carrier} from '../../../domain/carrier';

import shortUuid from 'short-uuid';
import {filter} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {CoreConfigurationService} from '../../../core-configuration.service';
import {ShipmentListComponent} from '../shipment-list/shipment-list.component';
import {ConfirmationDialogComponent} from '../../../component/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {Session} from '../../../domain/session';
import {Department} from '../../../domain/department';
import {MessageService} from '../../../component/message.service';
import {DateRange} from '../../../domain/date-range';
import {ShipmentListSettingsService} from './shipment-list-settings.service';
import {core} from '@angular/compiler';
import {RequestExecutor} from '../../../util/request-executor';
import {ShipmentStatusFilter} from './shipment-status-filter';

@Component({
  selector: 'app-all-shipments',
  templateUrl: './all-shipments.component.html',
  styleUrls: ['./all-shipments.component.css'],
  standalone: false
})
export class AllShipmentsComponent implements OnInit {
  totalShipmentCount: number;
  shipments: Shipment[];
  carriers: Carrier[];
  departments: Department[];
  filterableStatuses: ShipmentStatusFilter[] = [ShipmentStatusFilter.NotStarted, ShipmentStatusFilter.Started, ShipmentStatusFilter.NotAssigned, ShipmentStatusFilter.Assigned, ShipmentStatusFilter.Finished];
  searchQuery: string;

  currentPage = 0;
  currentCarrier: Carrier = null;
  currentDepartment: Department = null;
  currentStatus: ShipmentStatusFilter;
  currentSortBy: string;
  currentSortDirection: string;
  currentPageSize = 20;
  currentDateFilterValue: DateRange = new DateRange();

  multiple = false;
  progress = false;

  selectedShipments: Shipment[] = [];

  @ViewChild(ShipmentListComponent) list;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public coreConfiguration: CoreConfigurationService,
    session: Session,
    private dialog: MatDialog,
    private messageService: MessageService,
    private shipmentListSettings: ShipmentListSettingsService,
    private request: RequestExecutor
  ) {
    this.carriers = session.getCarriers().filter(value => value.Active);
    this.departments = session.getDepartments().filter(value => value.Active);
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      if (params['page'] !== undefined) {
        this.currentPage = +params['page'] - 1;
      } else {
        this.currentPage = 0;
      }

      this.currentCarrier = this.getCarrierById(params['carrier']);
      this.currentDepartment = this.getDepartmentById(params['department']);
      this.currentStatus = params['status'];
      this.currentPageSize = params['pageSize'] == null ? 20 : params['pageSize'];
      this.searchQuery = params['search'];

      let dateFilterValue = new DateRange();
      dateFilterValue.from = this.shipmentListSettings.getAllShipmentsDateFromFilterValue();
      dateFilterValue.to = this.shipmentListSettings.getAllShipmentsDateToFilterValue();
      this.currentDateFilterValue = dateFilterValue;
    });

    this.initValue();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.initValue();
      });
  }

  private getCarrierById(id: string) {
    if (id === undefined) {
      return null;
    }

    let carrier = this.carriers.find(r => r.Id.toLowerCase() === shortUuid().toUUID(id));

    if (carrier === undefined) {
      return null;
    }
    return carrier;
  }

  private getDepartmentById(id: string) {
    if (id === undefined) {
      return null;
    }

    let department = this.departments.find(r => r.Id.toLowerCase() === shortUuid().toUUID(id));

    if (department === undefined) {
      return null;
    }
    return department;
  }

  private initValue() {
    this.totalShipmentCount = this.route.snapshot.data['shipments'].totalShipmentCount;
    this.shipments = this.route.snapshot.data['shipments'].shipments;
  }

  changeFilteredStatus(status: ShipmentStatusFilter) {
    this.currentPage = 0;
    this.currentStatus = status;
    this.navigateWithNewParameters();
  }

  changeFilteredCarrier(carrier: Carrier) {
    this.currentPage = 0;
    this.currentCarrier = carrier;
    this.navigateWithNewParameters();
  }

  changeFilteredDepartment(department: Department) {
    this.currentPage = 0;
    this.currentDepartment = department;
    this.navigateWithNewParameters();
  }

  changePage(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.navigateWithNewParameters();
  }

  navigateWithNewParameters() {
    console.log('nav');
    const params = {
      page: this.currentPage + 1,
      pageSize: this.currentPageSize,
      status: this.currentStatus,
      sortBy: this.currentSortBy,
      sortDirection: this.currentSortDirection,
      search: this.searchQuery
    };

    if (this.currentCarrier !== null) {
      params['carrier'] = shortUuid().fromUUID(this.currentCarrier.Id);
    }

    if (this.currentDepartment !== null) {
      params['department'] = shortUuid().fromUUID(this.currentDepartment.Id);
    }

    this.clearSelection();
    this.multiple = false;

    this.router.navigate(['admin', 'shipments'], {queryParams: params});
  }

  sortChanged(sort: Sort) {
    this.currentSortBy = sort.active;
    this.currentSortDirection = sort.direction === 'desc' ? 'Desc' : 'Asc';

    this.navigateWithNewParameters();
  }

  showShipment(row: Shipment) {
    this.router.navigate(['admin', 'shipment', row.Number]);
  }

  toggleMultipleSelect() {
    this.multiple = !this.multiple;

    this.selectedShipments = [];
  }

  selectionToggled(shipment: Shipment) {
    let existing = this.selectedShipments.findIndex(s => s.Number === shipment.Number);

    if (existing === -1) {
      this.selectedShipments.push(shipment);
    } else {
      this.selectedShipments.splice(existing, 1);
    }
  }

  queryDeleteSelectedShipments() {
    let dialogRef = this.dialog.open(ConfirmationDialogComponent);

    dialogRef.componentInstance.title = 'Bekreft sletting';
    dialogRef.componentInstance.description = 'Vil du slette sendingen' + (this.selectedShipments.length > 1 ? 'e' : '') + '?';

    dialogRef.afterClosed()
      .subscribe(value => {
        if (value) {
          this.unassignShipments(this.selectedShipments.map(s => s.OrderId));
        }
      });
  }

  unassignShipments(orderIds: string[]) {
    this.progress = true;

    this.request.post('/order/delete/batch', {OrderIds: orderIds})
      .then(() => {
        this.messageService.showBottomSuccessSnackBar('Sendingen' + (this.selectedShipments.length > 1 ? 'e' : '') + ' ble slettet');

        this.navigateWithNewParameters();
      })
      .catch(error => {
        if (error.status == 401) {
          this.router.navigate(['login']);
        } else {
          this.messageService.showBottomErrorSnackBar('Kunne ikke slette sendingen' + (this.selectedShipments.length > 1 ? 'e' : ''));
        }
      })
      .finally(() => this.progress = false);
  }

  clearSelection() {
    this.list.clearSelection();
    this.selectedShipments = [];
  }

  currentDateValueChanged(value: DateRange) {
    if (!this.datesAreEqual(this.shipmentListSettings.getAllShipmentsDateToFilterValue(), value.to) || !this.datesAreEqual(this.shipmentListSettings.getAllShipmentsDateFromFilterValue(), value.from)) {
      this.currentDateFilterValue = value;

      this.shipmentListSettings.setAllShipmentsDateToFilterValue(value.to);
      this.shipmentListSettings.setAllShipmentsDateFromFilterValue(value.from);

      this.navigateWithNewParameters();
    }
  }

  private datesAreEqual(date1: Date, date2: Date): boolean {
    if (date1 != null && date2 != null) {
      return date1.getTime() === date2.getTime();
    }
    return date1 == null && date2 == null;
  }

  protected readonly core = core;
}
